'use client';

import { c as _c } from "react/compiler-runtime";
import { PagesResizableSheet } from '@/domains/pages/shared/pages-resizable-sheet';
import { buildGlobalDrawerSubtitle, buildTaskDrawerSubtitle, SavingsDetailsContent } from '@/domains/pages/model-savings/components/savings-details-content';
import { useDrawerCalculation } from '@/domains/pages/model-savings/hooks/use-task-drawer-calculation';
import type { SavingsScopeData, SavingsTask } from '@/domains/pages/model-savings/types';
import type { ModelSavingsTasksQueryScope } from '@/domains/pages/model-savings/utils/model-savings-api';
import { getTaskNeighborIndex, sortTasksBySaving } from '@/domains/pages/model-savings/utils/model-savings.utils';
import { useMemo } from 'react';
type SavingsGlobalDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: SavingsScopeData;
  queryScope: ModelSavingsTasksQueryScope | null;
};
export function SavingsGlobalDrawer(t0) {
  const $ = _c(16);
  const {
    open,
    onOpenChange,
    data,
    queryScope
  } = t0;
  let t1;
  if ($[0] !== open || $[1] !== queryScope) {
    t1 = {
      open,
      queryScope
    };
    $[0] = open;
    $[1] = queryScope;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const {
    calculationRows,
    totalActualSpend,
    totalOptimalSpend,
    isLoading: isCalculationLoading
  } = useDrawerCalculation(t1);
  let t2;
  if ($[3] !== data) {
    t2 = buildGlobalDrawerSubtitle(data);
    $[3] = data;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== calculationRows || $[6] !== data || $[7] !== isCalculationLoading || $[8] !== t2 || $[9] !== totalActualSpend || $[10] !== totalOptimalSpend) {
    t3 = <SavingsDetailsContent title="Model savings" subtitle={t2} data={data} calculationRows={calculationRows} calculationTotalActualSpend={totalActualSpend} calculationTotalOptimalSpend={totalOptimalSpend} isCalculationLoading={isCalculationLoading} />;
    $[5] = calculationRows;
    $[6] = data;
    $[7] = isCalculationLoading;
    $[8] = t2;
    $[9] = totalActualSpend;
    $[10] = totalOptimalSpend;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] !== onOpenChange || $[13] !== open || $[14] !== t3) {
    t4 = <PagesResizableSheet open={open} onOpenChange={onOpenChange}>{t3}</PagesResizableSheet>;
    $[12] = onOpenChange;
    $[13] = open;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  return t4;
}
type SavingsTaskDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tasks: SavingsTask[];
  selectedTaskId: string | null;
  onSelectedTaskIdChange: (taskId: string) => void;
  queryScope: ModelSavingsTasksQueryScope | null;
};
export function SavingsTaskDrawer(t0) {
  const $ = _c(26);
  const {
    open,
    onOpenChange,
    tasks,
    selectedTaskId,
    onSelectedTaskIdChange,
    queryScope
  } = t0;
  let t1;
  if ($[0] !== tasks) {
    t1 = sortTasksBySaving(tasks);
    $[0] = tasks;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const sortedTasks = t1;
  let t2;
  if ($[2] !== selectedTaskId) {
    t2 = task => task.id === selectedTaskId;
    $[2] = selectedTaskId;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const selectedTask = sortedTasks.find(t2);
  const selectedIndex = selectedTask ? getTaskNeighborIndex(sortedTasks, selectedTask.id) : -1;
  const {
    calculationRows,
    totalActualSpend,
    totalOptimalSpend,
    isLoading: isCalculationLoading
  } = useDrawerCalculation({
    open,
    queryScope,
    topicName: selectedTask?.name
  });
  if (!selectedTask) {
    return null;
  }
  const T0 = PagesResizableSheet;
  const T1 = SavingsDetailsContent;
  const t3 = selectedTask.name;
  const t4 = buildTaskDrawerSubtitle(selectedTask);
  const t5 = selectedIndex > 0;
  const t6 = selectedIndex >= 0 && selectedIndex < sortedTasks.length - 1;
  let t7;
  let t8;
  if ($[4] !== onSelectedTaskIdChange || $[5] !== selectedIndex || $[6] !== sortedTasks) {
    t7 = () => {
      const prev = sortedTasks[selectedIndex - 1];
      if (prev) {
        onSelectedTaskIdChange(prev.id);
      }
    };
    t8 = () => {
      const next = sortedTasks[selectedIndex + 1];
      if (next) {
        onSelectedTaskIdChange(next.id);
      }
    };
    $[4] = onSelectedTaskIdChange;
    $[5] = selectedIndex;
    $[6] = sortedTasks;
    $[7] = t7;
    $[8] = t8;
  } else {
    t7 = $[7];
    t8 = $[8];
  }
  let t9;
  if ($[9] !== T1 || $[10] !== calculationRows || $[11] !== isCalculationLoading || $[12] !== selectedTask || $[13] !== t4 || $[14] !== t5 || $[15] !== t6 || $[16] !== t7 || $[17] !== t8 || $[18] !== totalActualSpend || $[19] !== totalOptimalSpend) {
    t9 = <T1 title={t3} subtitle={t4} data={selectedTask} calculationRows={calculationRows} calculationTotalActualSpend={totalActualSpend} calculationTotalOptimalSpend={totalOptimalSpend} isCalculationLoading={isCalculationLoading} showNavigation={true} canGoPrev={t5} canGoNext={t6} onPrev={t7} onNext={t8} />;
    $[9] = T1;
    $[10] = calculationRows;
    $[11] = isCalculationLoading;
    $[12] = selectedTask;
    $[13] = t4;
    $[14] = t5;
    $[15] = t6;
    $[16] = t7;
    $[17] = t8;
    $[18] = totalActualSpend;
    $[19] = totalOptimalSpend;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== T0 || $[22] !== onOpenChange || $[23] !== open || $[24] !== t9) {
    t10 = <T0 open={open} onOpenChange={onOpenChange}>{t9}</T0>;
    $[21] = T0;
    $[22] = onOpenChange;
    $[23] = open;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
}